import {
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
} from '@mui/material';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';

import { useRecoilState, useSetRecoilState } from 'recoil';
import { toastStateSelector, ToastSeverity, dialogState } from 'state';

import { GeneralIds, ModelTypes, RoutePaths } from 'types/types';
import { useNavigate, useParams } from 'react-router-dom';
import { mutation } from 'operations';
import { FormTemplateType } from 'api/models';
import { getRoutePath } from 'utils';
import * as yup from 'yup';
import { FormTemplateEditorMode } from 'routes/FormTemplateEditor/FormTemplateEditor';

export function AddFormTemplateTypeDialog() {
  const [{ data }, closeDialog] = useRecoilState(dialogState);
  const mode: FormTemplateEditorMode = data?.mode;
  const setToast = useSetRecoilState(toastStateSelector);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { projectId } = useParams<GeneralIds>();
  const isFormMode = mode === FormTemplateEditorMode.default;

  const formik = useFormik({
    initialValues: {
      name: '',
    },
    validationSchema: yup.object({
      name: yup.string().required(t('general.errors.required') ?? ''),
    }),

    onSubmit: async (values, { resetForm }) => {
      const { name } = values;

      try {
        const formTemplate = await mutation<FormTemplateType>({
          type: ModelTypes.formTemplateType,
          body: {
            id: '',
            name,
            type: mode,
          },
          ids: {
            projectId,
          },
        });

        resetForm();
        closeDialog({ isOpen: false });

        const path = getRoutePath(
          isFormMode
            ? RoutePaths.formTemplateEditor
            : RoutePaths.sampleTemplateEditor,
          {
            formTemplateTypeId: formTemplate?.data?.id,
            projectId,
          },
        );
        navigate(path);
      } catch (e: any) {
        const errorCode = e.response?.data?.error?.[0].code;
        setToast({
          isOpen: true,
          message: i18n.exists(`errors.${errorCode}`)
            ? t(`errors.${errorCode}`)
            : t('general.errors.save'),
          severity: ToastSeverity.error,
        });
      }
    },
  });

  return (
    <Box>
      <DialogTitle>
        {isFormMode
          ? t('dialogs.addFormTemplateType.title')
          : t('dialogs.addFormTemplateType.sampleTitle')}
      </DialogTitle>
      <DialogContent>
        <form>
          <TextField
            sx={{ mt: 1 }}
            fullWidth
            label={t('general.name') + ' *'}
            type="name"
            name="name"
            value={formik.values.name}
            onChange={formik.handleChange}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </form>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => closeDialog({ isOpen: false })}
          color="secondary"
          variant="contained"
          sx={{ mb: 1 }}
        >
          {t('general.cancel')}
        </Button>
        <Button
          type="submit"
          color="primary"
          variant="contained"
          onClick={formik.submitForm}
          sx={{ mr: 2, mb: 1 }}
        >
          {t('general.create')}
        </Button>
      </DialogActions>
    </Box>
  );
}
